import { useEffect, useMemo, useState } from "react";
import {
  X, Zap, ArrowUpRight, ArrowDownRight, Clock, AlertTriangle, Ban,
  MoonStar, Loader2, Info, ListPlus, TimerReset,
} from "lucide-react";
import { api } from "@/lib/api";
import TradeRegisterDialog from "@/components/TradeRegisterDialog";

/**
 * Phase I13 — TradeNowDialog
 *
 * One-click answer over the intraday What Now scan. Renders two side-by-side
 * cards (BUY NOW | SHORT NOW) with entry levels, holding-period estimate, and
 * a Track-this-trade shortcut that pre-fills the existing register dialog.
 *
 * Honesty rules per I10: holding estimate is DESCRIPTIVE, not a prediction.
 */

function fmt(v, d = 2) {
  if (v == null || Number.isNaN(Number(v))) return "—";
  return Number(v).toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
}

function fmtHHMM(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString("en-US", {
      hour: "2-digit", minute: "2-digit", timeZone: "America/New_York",
    });
  } catch { return ""; }
}

function useCountdown(msTs) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!msTs) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [msTs]);
  if (!msTs) return null;
  const diff = Math.max(0, msTs - now);
  const mins = Math.floor(diff / 60000);
  const secs = Math.floor((diff % 60000) / 1000);
  return { mins, secs, total_ms: diff };
}

export default function TradeNowDialog({ open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [registerDefaults, setRegisterDefaults] = useState(null);

  const load = async (refresh = false) => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get("/intraday/trade-now",
        { params: refresh ? { refresh: true } : {} });
      setData(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load Trade Now.");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (open) load(false);
    else { setData(null); setErr(""); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const state = data?.state;
  const long = data?.top_long_pick;
  const short = data?.top_short_pick;

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm flex items-start justify-center overflow-y-auto"
        onClick={() => onOpenChange(false)}
        data-testid="trade-now-dialog-backdrop"
      >
        <div
          onClick={(e) => e.stopPropagation()}
          data-testid="trade-now-dialog"
          className="w-full max-w-[1200px] mx-3 my-8 border border-[#1E293B] bg-[#0B111C] mono text-slate-200 shadow-2xl"
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-[#1E293B] bg-[#0F1626] flex items-center gap-3">
            <Zap className="h-4 w-4 text-amber-400" />
            <span className="mono text-[11px] uppercase tracking-widest text-amber-300 font-bold">
              Trade Now
            </span>
            <span className="mono text-[10px] uppercase tracking-widest text-slate-500">
              · one-click intraday top pick
            </span>
            <div className="ml-auto flex items-center gap-2">
              <button
                data-testid="trade-now-refresh"
                onClick={() => load(true)}
                disabled={loading}
                className="mono text-[10px] uppercase tracking-widest px-2 h-7 border border-cyan-500/50 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-200 disabled:opacity-50 flex items-center gap-1"
              >
                {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <TimerReset className="h-3 w-3" />}
                Refresh
              </button>
              <button
                data-testid="trade-now-close"
                onClick={() => onOpenChange(false)}
                className="p-1 text-slate-500 hover:text-slate-200"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="p-4 space-y-3">
            {loading && !data && (
              <div className="p-8 text-center mono text-[11px] text-slate-500 flex items-center justify-center gap-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading intraday scan…
              </div>
            )}
            {err && (
              <div data-testid="trade-now-error" className="border border-red-500/40 bg-red-500/10 p-3 mono text-[11px] text-red-300">
                {err}
              </div>
            )}

            {data && state === "MARKET_CLOSED" && (
              <SessionBanner
                testid="trade-now-closed"
                Icon={MoonStar}
                title={`Market closed · ${(data.session || "").replace("_", "-") || "closed"}`}
                body={data.closed_message + (data.next_open ? ` Next open: ${fmtHHMM(data.next_open)} ET.` : "")}
                tone="slate"
              />
            )}
            {data && state === "NO_ENTRY_WINDOW" && (
              <SessionBanner
                testid="trade-now-no-entry-window"
                Icon={Ban}
                title="Entries closed for the day"
                body={data.closed_message}
                tone="amber"
              />
            )}

            {data && state === "LIVE" && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <PickCard
                  pick={long}
                  side="LONG"
                  label="BUY NOW"
                  onTrack={setRegisterDefaults}
                  computedAt={data.computed_at}
                  Icon={ArrowUpRight}
                />
                <PickCard
                  pick={short}
                  side="SHORT"
                  label="SHORT NOW"
                  onTrack={setRegisterDefaults}
                  computedAt={data.computed_at}
                  Icon={ArrowDownRight}
                />
              </div>
            )}

            {/* Descriptive/honesty notice is shown in EVERY state, not only LIVE */}
            {data && <DescriptiveFooter notice={data.descriptive_notice} />}
          </div>
        </div>
      </div>

      <TradeRegisterDialog
        open={!!registerDefaults}
        onOpenChange={(v) => { if (!v) setRegisterDefaults(null); }}
        defaults={registerDefaults}
        onCreated={() => setRegisterDefaults(null)}
      />
    </>
  );
}

function SessionBanner({ testid, Icon, title, body, tone }) {
  const cls = {
    slate:  "border-slate-600     bg-slate-800/50  text-slate-300",
    amber:  "border-amber-500/50  bg-amber-500/10  text-amber-200",
  }[tone] || "border-slate-600 bg-slate-800/50 text-slate-300";
  return (
    <div data-testid={testid} className={`border ${cls} p-4 flex items-start gap-3`}>
      <Icon className="h-5 w-5 shrink-0 mt-0.5" />
      <div>
        <div className="mono text-[11px] uppercase tracking-widest font-bold">{title}</div>
        <div className="mono text-[11px] mt-1 opacity-90">{body}</div>
      </div>
    </div>
  );
}

function DescriptiveFooter({ notice }) {
  return (
    <div className="border border-[#1E293B] bg-[#0F1626]/60 px-3 py-2 flex items-start gap-2" data-testid="trade-now-descriptive-footer">
      <Info className="h-3.5 w-3.5 text-slate-500 shrink-0 mt-0.5" />
      <div className="mono text-[10px] text-slate-500 uppercase tracking-widest leading-relaxed">
        {notice}
      </div>
    </div>
  );
}

function PickCard({ pick, side, label, onTrack, computedAt, Icon }) {
  const testid = `trade-now-card-${side.toLowerCase()}`;
  const isLong = side === "LONG";
  const accent = isLong
    ? { border: "border-emerald-500/50", bg: "bg-emerald-500/5", text: "text-emerald-300" }
    : { border: "border-red-500/50",     bg: "bg-red-500/5",     text: "text-red-300" };

  const countdown = useCountdown(pick?.time_exit_ts_ms || null);

  if (!pick) {
    return (
      <div data-testid={testid} data-state="empty" className={`border ${accent.border} ${accent.bg}`}>
        <CardHeader label={label} side={side} accent={accent} Icon={Icon} />
        <div className="p-4 mono text-[11px] text-slate-500 flex items-start gap-2 border-t border-[#1E293B]">
          <Info className="h-3.5 w-3.5 text-slate-500 shrink-0 mt-0.5" />
          <div>
            No {side.toLowerCase()} candidate exists in the current scan window.
          </div>
        </div>
      </div>
    );
  }

  const notQualified = !!pick.not_qualified;
  const beyond = pick.beyond_chase?.beyond;
  const holding = pick.holding_period;
  const score = isLong ? pick.long_score : pick.short_score;
  const stateAttr = beyond ? "beyond-chase" : notQualified ? "not-qualified" : "ok";

  const handleTrack = () => {
    onTrack({
      ticker: pick.ticker,
      direction: side,
      entry_price: pick.ideal_entry ?? pick.price,
      initial_stop: pick.stop_loss,
      targets: { t1: pick.t1, t2: pick.t2 },
      time_exit_ts_ms: pick.time_exit_ts_ms || null,
      source_snapshot_id: null,
    });
  };

  return (
    <div data-testid={testid} data-state={stateAttr} className={`border ${accent.border} ${accent.bg}`}>
      <CardHeader label={label} side={side} accent={accent} Icon={Icon} />

      {notQualified && (
        <div data-testid={`${testid}-not-qualified`} className="px-3 py-2 border-t border-b border-slate-500/40 bg-slate-500/10 mono text-[10px] uppercase tracking-widest text-slate-300 flex items-start gap-2">
          <AlertTriangle className="h-3.5 w-3.5 text-slate-400 shrink-0 mt-0.5" />
          <div>
            No qualifying trade — closest candidate (below threshold)
            <div className="text-slate-500 normal-case tracking-normal mt-0.5">{pick.not_qualified_reason}</div>
          </div>
        </div>
      )}

      <div className="p-4 border-t border-[#1E293B]">
        {/* Ticker + price row */}
        <div className="flex items-baseline gap-3 flex-wrap">
          <span data-testid={`${testid}-ticker`} className="mono text-3xl font-extrabold text-slate-100 tracking-tight">{pick.ticker}</span>
          {pick.company_name && (
            <span className="mono text-[10px] uppercase tracking-widest text-slate-500 truncate max-w-[220px]">
              {pick.company_name}
            </span>
          )}
          <span className={`mono text-2xl font-bold tabular-nums ml-auto ${accent.text}`}>${fmt(pick.price)}</span>
        </div>
        <div className="mono text-[10px] uppercase tracking-widest text-slate-500 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
          <span data-testid={`${testid}-descriptive-chip`} className="px-1.5 py-0.5 border border-slate-600 text-slate-400">Descriptive</span>
          <span>Score {fmt(score, 1)}</span>
          <span>{pick.strength_band}</span>
          {pick.confidence && <span>Conf {pick.confidence}</span>}
          {pick.risk_reward_t1 != null && <span>R/R {fmt(pick.risk_reward_t1, 2)}</span>}
        </div>

        {/* Beyond-chase warning replaces holding badge if triggered */}
        {beyond ? (
          <div data-testid={`${testid}-beyond-chase`} className="mt-3 border border-amber-500/60 bg-amber-500/10 p-3 mono text-[11px] text-amber-200 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <div>
              <div className="font-bold uppercase tracking-widest text-[10px]">Price past chase limit — do not enter</div>
              <div className="text-amber-200/90 mt-0.5 normal-case tracking-normal">{pick.beyond_chase.message}</div>
            </div>
          </div>
        ) : (
          <div className="mt-3 flex flex-wrap items-center gap-3">
            {holding?.classification && (
              <div data-testid={`${testid}-holding-badge`} data-classification={holding.classification} className="border border-cyan-500/50 bg-cyan-500/10 px-2.5 py-1.5 flex items-center gap-2">
                <Clock className="h-3.5 w-3.5 text-cyan-300" />
                <span className="mono text-[10px] uppercase tracking-widest text-cyan-200">Holding</span>
                <span className="mono text-[13px] font-bold text-cyan-100 tabular-nums">
                  {holding.classification}
                </span>
              </div>
            )}
            {countdown && (
              <div data-testid={`${testid}-time-exit`} className="mono text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1">
                Time-exit: <span className="text-slate-300 tabular-nums font-bold">
                  {String(countdown.mins).padStart(2, "0")}:{String(countdown.secs).padStart(2, "0")}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Levels grid */}
        <div className="mt-3 grid grid-cols-2 gap-2">
          <LevelBox label="Ideal entry" value={pick.ideal_entry} testid={`${testid}-ideal`} />
          <LevelBox label="Do-not-chase" value={pick.do_not_chase} testid={`${testid}-dnc`} />
          <LevelBox label={`Zone (${fmt(pick.entry_zone?.low)}–${fmt(pick.entry_zone?.high)})`} value={null} testid={`${testid}-zone`} span={2} />
          <LevelBox label="Stop" value={pick.stop_loss} tone="red" testid={`${testid}-stop`} />
          <LevelBox label="T1" value={pick.t1} tone={isLong ? "emerald" : "red"} testid={`${testid}-t1`} />
          <LevelBox label="T2" value={pick.t2} tone={isLong ? "emerald" : "red"} testid={`${testid}-t2`} span={2} />
        </div>

        {/* Top factor + invalidation */}
        <div className="mt-3 space-y-1">
          {pick.top_factor && (
            <div className="mono text-[10px] uppercase tracking-widest text-cyan-300" data-testid={`${testid}-factor`}>
              ▸ {pick.top_factor}
            </div>
          )}
          {pick.invalidation?.human && (
            <div className="mono text-[10px] text-slate-500" data-testid={`${testid}-invalidation`}>
              Invalidation: <span className="text-slate-300">{pick.invalidation.human}</span>
            </div>
          )}
        </div>

        {/* Track-this-trade */}
        <div className="mt-4 pt-3 border-t border-[#1E293B] flex items-center justify-between gap-2">
          <span className="mono text-[9px] uppercase tracking-widest text-slate-600">
            No validated edge · descriptive per I10
          </span>
          <button
            data-testid={`${testid}-track`}
            disabled={beyond}
            onClick={handleTrack}
            className={`mono text-[11px] uppercase tracking-widest px-3 h-8 border flex items-center gap-2 ${
              beyond
                ? "border-[#1E293B] text-slate-600 cursor-not-allowed"
                : isLong
                  ? "border-emerald-500/60 bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-200"
                  : "border-red-500/60 bg-red-500/15 hover:bg-red-500/25 text-red-200"
            }`}
          >
            <ListPlus className="h-3.5 w-3.5" />
            Track this trade
          </button>
        </div>
      </div>
    </div>
  );
}

function CardHeader({ label, side, accent, Icon }) {
  return (
    <div className={`px-3 py-2 flex items-center gap-2 border-b border-[#1E293B] ${accent.bg}`}>
      {Icon && <Icon className={`h-3.5 w-3.5 ${accent.text}`} />}
      <span className={`mono text-[11px] uppercase tracking-widest font-bold ${accent.text}`}>{label}</span>
      <span className="mono text-[9px] uppercase tracking-widest text-slate-500 ml-auto">{side}</span>
    </div>
  );
}

function LevelBox({ label, value, tone, testid, span = 1 }) {
  const toneCls = {
    red:     "text-red-300",
    emerald: "text-emerald-300",
  }[tone] || "text-slate-200";
  return (
    <div data-testid={testid} className={`border border-[#1E293B] bg-[#0B111C] px-2 py-1.5 ${span === 2 ? "col-span-2" : ""}`}>
      <div className="mono text-[9px] uppercase tracking-widest text-slate-500">{label}</div>
      {value != null && <div className={`mono text-[13px] font-bold tabular-nums ${toneCls}`}>${fmt(value)}</div>}
    </div>
  );
}
